import type { UiLanguageCode } from '@/lib/ui-languages';

/** Voix off texte, visuel IA (viewport), branches parallèles rejouables — 12 langues UI. */
export const playerVisualParallelLocales: Record<UiLanguageCode, Record<string, string>> = {
  fr: {
    'eduflow.designer.textVoiceoverHint':
      'La voix off lit la description et le contenu du bloc.',
    'player.visualAi.emptyTitle': 'Aucun visuel IA configuré',
    'player.visualAi.emptyHint':
      'Revenez dans le concepteur pour générer ou configurer ce visuel.',
    'player.visualAi.defaultTitle': 'Visuel IA',
    'player.visualAi.subtitle':
      'Explorez le diagramme : zoomez, faites défiler ou utilisez le mode plein écran.',
    'player.visualAi.openFullscreen': 'Ouvrir en plein écran',
    'player.visualAi.closeFullscreen': 'Fermer le plein écran',
    'player.visualAi.badge': 'Visuel IA',
    'player.visualAi.viewportLabel': 'Diagramme interactif — zoom et déplacement',
    'player.visualAi.toolbarLabel': 'Contrôles du diagramme',
    'player.visualAi.zoomOut': 'Zoom arrière',
    'player.visualAi.zoomIn': 'Zoom avant',
    'player.visualAi.resetZoom': 'Réinitialiser le zoom à 100 %',
    'player.visualAi.reset': '100 %',
    'player.visualAi.fitView': 'Ajuster le diagramme à la fenêtre',
    'player.visualAi.fit': 'Ajuster',
    'player.visualAi.zoomLevel': 'Zoom {{percent}} %',
    'player.visualAi.viewportHint':
      'Molette + Ctrl (ou Cmd) pour zoomer. Flèches du clavier pour vous déplacer une fois la zone focalisée. Tab pour atteindre les contrôles.',
    'player.parallelBranchesPickHint':
      'Les voies terminées restent cliquables pour les revoir. Parcourez chaque branche au moins une fois avant la suite commune.',
    'player.parallelBranchActionReplay': 'Refaire cette voie',
  },
  en: {
    'eduflow.designer.textVoiceoverHint':
      'The voiceover reads both the block description and main content.',
    'player.visualAi.emptyTitle': 'No AI visual configured',
    'player.visualAi.emptyHint':
      'Return to the designer to generate or configure this visual.',
    'player.visualAi.defaultTitle': 'AI visual',
    'player.visualAi.subtitle':
      'Explore the diagram: zoom, scroll, or use full screen mode.',
    'player.visualAi.openFullscreen': 'Open full screen',
    'player.visualAi.closeFullscreen': 'Close full screen',
    'player.visualAi.badge': 'AI visual',
    'player.visualAi.viewportLabel': 'Interactive diagram — zoom and pan',
    'player.visualAi.toolbarLabel': 'Diagram controls',
    'player.visualAi.zoomOut': 'Zoom out',
    'player.visualAi.zoomIn': 'Zoom in',
    'player.visualAi.resetZoom': 'Reset zoom to 100%',
    'player.visualAi.reset': '100%',
    'player.visualAi.fitView': 'Fit diagram to window',
    'player.visualAi.fit': 'Fit',
    'player.visualAi.zoomLevel': 'Zoom {{percent}}%',
    'player.visualAi.viewportHint':
      'Ctrl (or Cmd) + scroll wheel to zoom. Arrow keys to pan when the viewport is focused. Tab to reach the controls.',
    'player.parallelBranchesPickHint':
      'Completed paths stay clickable to review. Finish each branch at least once before the shared section.',
    'player.parallelBranchActionReplay': 'Retake this path',
  },
  es: {
    'eduflow.designer.textVoiceoverHint':
      'La locución lee la descripción y el contenido del bloque.',
    'player.visualAi.emptyTitle': 'Ningún visual de IA configurado',
    'player.visualAi.emptyHint':
      'Vuelva al diseñador para generar o configurar este visual.',
    'player.visualAi.defaultTitle': 'Visual de IA',
    'player.visualAi.subtitle':
      'Explore el diagrama: zoom, desplazamiento o modo pantalla completa.',
    'player.visualAi.openFullscreen': 'Abrir pantalla completa',
    'player.visualAi.closeFullscreen': 'Cerrar pantalla completa',
    'player.visualAi.badge': 'Visual de IA',
    'player.visualAi.viewportLabel': 'Diagrama interactivo — zoom y desplazamiento',
    'player.visualAi.toolbarLabel': 'Controles del diagrama',
    'player.visualAi.zoomOut': 'Alejar',
    'player.visualAi.zoomIn': 'Acercar',
    'player.visualAi.resetZoom': 'Restablecer zoom al 100 %',
    'player.visualAi.reset': '100 %',
    'player.visualAi.fitView': 'Ajustar el diagrama a la ventana',
    'player.visualAi.fit': 'Ajustar',
    'player.visualAi.zoomLevel': 'Zoom {{percent}} %',
    'player.visualAi.viewportHint':
      'Ctrl (o Cmd) + rueda para zoom. Flechas para desplazarse con el área enfocada. Tab para los controles.',
    'player.parallelBranchesPickHint':
      'Las rutas completadas siguen siendo clicables para repasarlas. Recorra cada rama al menos una vez antes de la parte común.',
    'player.parallelBranchActionReplay': 'Repetir esta vía',
  },
  de: {
    'eduflow.designer.textVoiceoverHint':
      'Die Voiceover liest sowohl die Beschreibung als auch den Hauptinhalt des Blocks.',
    'player.visualAi.emptyTitle': 'Kein KI-Visual konfiguriert',
    'player.visualAi.emptyHint':
      'Kehren Sie zum Designer zurück, um dieses Visual zu erstellen oder zu konfigurieren.',
    'player.visualAi.defaultTitle': 'KI-Visual',
    'player.visualAi.subtitle':
      'Diagramm erkunden: zoomen, scrollen oder Vollbildmodus nutzen.',
    'player.visualAi.openFullscreen': 'Vollbild öffnen',
    'player.visualAi.closeFullscreen': 'Vollbild schließen',
    'player.visualAi.badge': 'KI-Visual',
    'player.visualAi.viewportLabel': 'Interaktives Diagramm — Zoomen und Verschieben',
    'player.visualAi.toolbarLabel': 'Diagrammsteuerung',
    'player.visualAi.zoomOut': 'Verkleinern',
    'player.visualAi.zoomIn': 'Vergrößern',
    'player.visualAi.resetZoom': 'Zoom auf 100 % zurücksetzen',
    'player.visualAi.reset': '100 %',
    'player.visualAi.fitView': 'Diagramm an Fenster anpassen',
    'player.visualAi.fit': 'Anpassen',
    'player.visualAi.zoomLevel': 'Zoom {{percent}} %',
    'player.visualAi.viewportHint':
      'Strg (oder Cmd) + Mausrad zum Zoomen. Pfeiltasten zum Verschieben, wenn der Bereich fokussiert ist. Tab für die Steuerung.',
    'player.parallelBranchesPickHint':
      'Abgeschlossene Wege bleiben anklickbar zur Wiederholung. Durchlaufen Sie jeden Zweig mindestens einmal vor dem gemeinsamen Abschnitt.',
    'player.parallelBranchActionReplay': 'Diesen Weg wiederholen',
  },
  it: {
    'eduflow.designer.textVoiceoverHint':
      'La voce fuori campo legge la descrizione e il contenuto del blocco.',
    'player.visualAi.emptyTitle': 'Nessun visual IA configurato',
    'player.visualAi.emptyHint':
      'Torna al designer per generare o configurare questo visual.',
    'player.visualAi.defaultTitle': 'Visual IA',
    'player.visualAi.subtitle':
      'Esplora il diagramma: zoom, scorrimento o modalità schermo intero.',
    'player.visualAi.openFullscreen': 'Apri schermo intero',
    'player.visualAi.closeFullscreen': 'Chiudi schermo intero',
    'player.visualAi.badge': 'Visual IA',
    'player.visualAi.viewportLabel': 'Diagramma interattivo — zoom e spostamento',
    'player.visualAi.toolbarLabel': 'Controlli del diagramma',
    'player.visualAi.zoomOut': 'Riduci zoom',
    'player.visualAi.zoomIn': 'Aumenta zoom',
    'player.visualAi.resetZoom': 'Reimposta zoom al 100 %',
    'player.visualAi.reset': '100 %',
    'player.visualAi.fitView': 'Adatta il diagramma alla finestra',
    'player.visualAi.fit': 'Adatta',
    'player.visualAi.zoomLevel': 'Zoom {{percent}} %',
    'player.visualAi.viewportHint':
      'Ctrl (o Cmd) + rotella per lo zoom. Frecce per spostarsi con l’area a fuoco. Tab per i controlli.',
    'player.parallelBranchesPickHint':
      'I percorsi completati restano cliccabili per ripassarli. Percorri ogni ramo almeno una volta prima della parte comune.',
    'player.parallelBranchActionReplay': 'Ripeti questo percorso',
  },
  pt: {
    'eduflow.designer.textVoiceoverHint':
      'A locução lê a descrição e o conteúdo do bloco.',
    'player.visualAi.emptyTitle': 'Nenhum visual de IA configurado',
    'player.visualAi.emptyHint':
      'Volte ao designer para gerar ou configurar este visual.',
    'player.visualAi.defaultTitle': 'Visual de IA',
    'player.visualAi.subtitle':
      'Explore o diagrama: zoom, deslocamento ou modo de ecrã inteiro.',
    'player.visualAi.openFullscreen': 'Abrir ecrã inteiro',
    'player.visualAi.closeFullscreen': 'Fechar ecrã inteiro',
    'player.visualAi.badge': 'Visual de IA',
    'player.visualAi.viewportLabel': 'Diagrama interativo — zoom e deslocamento',
    'player.visualAi.toolbarLabel': 'Controlos do diagrama',
    'player.visualAi.zoomOut': 'Reduzir zoom',
    'player.visualAi.zoomIn': 'Aumentar zoom',
    'player.visualAi.resetZoom': 'Repor zoom a 100 %',
    'player.visualAi.reset': '100 %',
    'player.visualAi.fitView': 'Ajustar o diagrama à janela',
    'player.visualAi.fit': 'Ajustar',
    'player.visualAi.zoomLevel': 'Zoom {{percent}} %',
    'player.visualAi.viewportHint':
      'Ctrl (ou Cmd) + roda do rato para zoom. Setas para deslocar com a área focada. Tab para os controlos.',
    'player.parallelBranchesPickHint':
      'Os percursos concluídos continuam clicáveis para rever. Percorra cada ramo pelo menos uma vez antes da parte comum.',
    'player.parallelBranchActionReplay': 'Repetir este percurso',
  },
  lb: {
    'eduflow.designer.textVoiceoverHint':
      'D’Off-Stëmm liest souwuel d’Beschreiwung wéi den Haaptinhalt vum Block.',
    'player.visualAi.emptyTitle': 'Keen KI-Visual konfiguréiert',
    'player.visualAi.emptyHint':
      'Gitt zréck an den Designer fir dëst Visual ze generéieren oder ze konfiguréieren.',
    'player.visualAi.defaultTitle': 'KI-Visual',
    'player.visualAi.subtitle':
      'Diagramm entdecken: zoomen, scrollen oder Vollbildmodus benotzen.',
    'player.visualAi.openFullscreen': 'Vollbild opmaachen',
    'player.visualAi.closeFullscreen': 'Vollbild zoumaachen',
    'player.visualAi.badge': 'KI-Visual',
    'player.visualAi.viewportLabel': 'Interaktivt Diagramm — Zoomen a Verschieben',
    'player.visualAi.toolbarLabel': 'Diagrammsteierung',
    'player.visualAi.zoomOut': 'Verklengeren',
    'player.visualAi.zoomIn': 'Vergréisseren',
    'player.visualAi.resetZoom': 'Zoom op 100 % zrécksetzen',
    'player.visualAi.reset': '100 %',
    'player.visualAi.fitView': 'Diagramm un d’Fënster upassen',
    'player.visualAi.fit': 'Upassen',
    'player.visualAi.zoomLevel': 'Zoom {{percent}} %',
    'player.visualAi.viewportHint':
      'Strg (oder Cmd) + Mausrad fir ze zoomen. Feiltasten fir ze verschieben, wann de Beräich fokusséiert ass. Tab fir d’Steierung.',
    'player.parallelBranchesPickHint':
      'Ofgeschloss Weeër bleiwen klickbar fir ze widderhuelen. Duerchlaf all Branche mindestens eemol virun dem gemeinsame Deel.',
    'player.parallelBranchActionReplay': 'Dëse Wee widderhuelen',
  },
  uk: {
    'eduflow.designer.textVoiceoverHint':
      'Озвучення читає опис і вміст блоку.',
    'player.visualAi.emptyTitle': 'Візуал ШІ не налаштовано',
    'player.visualAi.emptyHint':
      'Поверніться до конструктора, щоб створити або налаштувати цей візуал.',
    'player.visualAi.defaultTitle': 'Візуал ШІ',
    'player.visualAi.subtitle':
      'Досліджуйте діаграму: масштаб, прокрутка або повноекранний режим.',
    'player.visualAi.openFullscreen': 'Відкрити на весь екран',
    'player.visualAi.closeFullscreen': 'Закрити повноекранний режим',
    'player.visualAi.badge': 'Візуал ШІ',
    'player.visualAi.viewportLabel': 'Інтерактивна діаграма — масштаб і переміщення',
    'player.visualAi.toolbarLabel': 'Керування діаграмою',
    'player.visualAi.zoomOut': 'Зменшити',
    'player.visualAi.zoomIn': 'Збільшити',
    'player.visualAi.resetZoom': 'Скинути масштаб до 100 %',
    'player.visualAi.reset': '100 %',
    'player.visualAi.fitView': 'Підігнати діаграму під вікно',
    'player.visualAi.fit': 'Підігнати',
    'player.visualAi.zoomLevel': 'Масштаб {{percent}} %',
    'player.visualAi.viewportHint':
      'Ctrl (або Cmd) + коліщатко миші для масштабу. Стрілки для переміщення після фокусу на області. Tab для елементів керування.',
    'player.parallelBranchesPickHint':
      'Завершені шляхи залишаються доступними для повторного перегляду. Пройдіть кожну гілку щонайменше раз перед спільною частиною.',
    'player.parallelBranchActionReplay': 'Пройти цей шлях знову',
  },
  ru: {
    'eduflow.designer.textVoiceoverHint':
      'Озвучка читает описание и содержимое блока.',
    'player.visualAi.emptyTitle': 'Визуал ИИ не настроен',
    'player.visualAi.emptyHint':
      'Вернитесь в конструктор, чтобы создать или настроить этот визуал.',
    'player.visualAi.defaultTitle': 'Визуал ИИ',
    'player.visualAi.subtitle':
      'Изучайте диаграмму: масштаб, прокрутка или полноэкранный режим.',
    'player.visualAi.openFullscreen': 'Открыть на весь экран',
    'player.visualAi.closeFullscreen': 'Закрыть полноэкранный режим',
    'player.visualAi.badge': 'Визуал ИИ',
    'player.visualAi.viewportLabel': 'Интерактивная диаграмма — масштаб и перемещение',
    'player.visualAi.toolbarLabel': 'Управление диаграммой',
    'player.visualAi.zoomOut': 'Уменьшить',
    'player.visualAi.zoomIn': 'Увеличить',
    'player.visualAi.resetZoom': 'Сбросить масштаб до 100 %',
    'player.visualAi.reset': '100 %',
    'player.visualAi.fitView': 'Подогнать диаграмму под окно',
    'player.visualAi.fit': 'Подогнать',
    'player.visualAi.zoomLevel': 'Масштаб {{percent}} %',
    'player.visualAi.viewportHint':
      'Ctrl (или Cmd) + колёсико мыши для масштаба. Стрелки для перемещения при фокусе на области. Tab для элементов управления.',
    'player.parallelBranchesPickHint':
      'Завершённые пути остаются доступными для повторного просмотра. Пройдите каждую ветку хотя бы раз перед общей частью.',
    'player.parallelBranchActionReplay': 'Пройти этот путь снова',
  },
  ar: {
    'eduflow.designer.textVoiceoverHint':
      'يقرأ التعليق الصوتي وصف القسم ومحتواه.',
    'player.visualAi.emptyTitle': 'لا يوجد مرئي ذكاء اصطناعي مُعدّ',
    'player.visualAi.emptyHint':
      'عودوا إلى المصمّم لإنشاء هذا المرئي أو تهيئته.',
    'player.visualAi.defaultTitle': 'مرئي ذكاء اصطناعي',
    'player.visualAi.subtitle':
      'استكشفوا المخطط: تكبير، تمرير أو وضع ملء الشاشة.',
    'player.visualAi.openFullscreen': 'فتح ملء الشاشة',
    'player.visualAi.closeFullscreen': 'إغلاق ملء الشاشة',
    'player.visualAi.badge': 'مرئي ذكاء اصطناعي',
    'player.visualAi.viewportLabel': 'مخطط تفاعلي — تكبير وتحريك',
    'player.visualAi.toolbarLabel': 'عناصر تحكم المخطط',
    'player.visualAi.zoomOut': 'تصغير',
    'player.visualAi.zoomIn': 'تكبير',
    'player.visualAi.resetZoom': 'إعادة التكبير إلى 100٪',
    'player.visualAi.reset': '100٪',
    'player.visualAi.fitView': 'ملاءمة المخطط مع النافذة',
    'player.visualAi.fit': 'ملاءمة',
    'player.visualAi.zoomLevel': 'تكبير {{percent}}٪',
    'player.visualAi.viewportHint':
      'Ctrl (أو Cmd) + عجلة الفأرة للتكبير. مفاتيح الأسهم للتحريك بعد تركيز المنطقة. Tab للوصول إلى عناصر التحكم.',
    'player.parallelBranchesPickHint':
      'المسارات المكتملة تبقى قابلة للنقر للمراجعة. اجتازوا كل فرع مرة واحدة على الأقل قبل الجزء المشترك.',
    'player.parallelBranchActionReplay': 'إعادة هذا المسار',
  },
  tr: {
    'eduflow.designer.textVoiceoverHint':
      'Seslendirme, bloğun açıklamasını ve içeriğini okur.',
    'player.visualAi.emptyTitle': 'Yapay zekâ görseli yapılandırılmamış',
    'player.visualAi.emptyHint':
      'Bu görseli oluşturmak veya yapılandırmak için tasarımcıya dönün.',
    'player.visualAi.defaultTitle': 'Yapay zekâ görseli',
    'player.visualAi.subtitle':
      'Diyagramı keşfedin: yakınlaştırma, kaydırma veya tam ekran.',
    'player.visualAi.openFullscreen': 'Tam ekranı aç',
    'player.visualAi.closeFullscreen': 'Tam ekranı kapat',
    'player.visualAi.badge': 'Yapay zekâ görseli',
    'player.visualAi.viewportLabel': 'Etkileşimli diyagram — yakınlaştırma ve kaydırma',
    'player.visualAi.toolbarLabel': 'Diyagram denetimleri',
    'player.visualAi.zoomOut': 'Uzaklaştır',
    'player.visualAi.zoomIn': 'Yakınlaştır',
    'player.visualAi.resetZoom': 'Yakınlaştırmayı %100’e sıfırla',
    'player.visualAi.reset': '%100',
    'player.visualAi.fitView': 'Diyagramı pencereye sığdır',
    'player.visualAi.fit': 'Sığdır',
    'player.visualAi.zoomLevel': 'Yakınlaştırma %{{percent}}',
    'player.visualAi.viewportHint':
      'Yakınlaştırmak için Ctrl (veya Cmd) + fare tekerleği. Odaklandıktan sonra ok tuşlarıyla kaydırma. Denetimler için Tab.',
    'player.parallelBranchesPickHint':
      'Tamamlanan yollar tekrar göz atmak için tıklanabilir kalır. Ortak bölümden önce her dalı en az bir kez tamamlayın.',
    'player.parallelBranchActionReplay': 'Bu yolu yeniden al',
  },
};
